import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

const LEVEL_TEXT = { edit: ["Can edit", "text-emerald-300"], view: ["Read only", "text-sky-300"], none: ["No access", "text-nex-muted"] };

export function LevelText({ level, testid }) {
  const [t, c] = LEVEL_TEXT[level || "none"];
  return <span data-testid={testid} className={`font-mono text-[10px] uppercase tracking-[0.14em] ${c}`}>{t}</span>;
}

function CardRow({ card, level, set, locked }) {
  const [open, setOpen] = useState(false);
  const see = !!level;
  return (
    <li data-testid={`am-card-${card.id}`} className="border-t border-white/5">
      <div className="flex items-center gap-3 px-3 py-2">
        <button type="button" onClick={() => setOpen(!open)} data-testid={`am-card-toggle-${card.id}`} className="flex-1 min-w-0 flex items-center gap-2 text-left text-sm text-nex-silver hover:text-white transition-colors">
          {open ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}<span className="truncate">{card.label}</span>
        </button>
        <LevelText level={level} testid={`am-card-level-${card.id}`} />
        <label className="inline-flex items-center gap-1.5 text-[11px] text-nex-muted"><Checkbox disabled={locked} checked={see} onCheckedChange={(v) => set(card.id, v ? "view" : null)} data-testid={`am-see-${card.id}`} />See</label>
        <label className={`inline-flex items-center gap-1.5 text-[11px] ${see ? "text-nex-muted" : "text-nex-muted/40"}`}><Checkbox disabled={locked || !see} checked={level === "edit"} onCheckedChange={(v) => set(card.id, v ? "edit" : "view")} data-testid={`am-edit-${card.id}`} />Edit</label>
      </div>
      {open && (
        <div data-testid={`am-card-detail-${card.id}`} className="pl-9 pr-3 pb-3 text-xs text-nex-muted space-y-1">
          <div>{level ? <>They see everything inside: <span className="text-nex-silver">{card.sections.join(" · ") || card.label}</span></> : "Hidden from their desk and blocked on the server."}</div>
          <div><LevelText level={level} /> {level === "view" && "— buttons that change data are disabled and the server refuses writes."}</div>
        </div>
      )}
    </li>
  );
}

export function EngineRow({ engine, access, set, setMany, locked }) {
  const [open, setOpen] = useState(false);
  const ids = engine.cards.map((c) => c.id);
  const seeAll = ids.every((id) => access[id]);
  const editAll = ids.every((id) => access[id] === "edit");
  const count = ids.filter((id) => access[id]).length;
  return (
    <div data-testid={`am-engine-${engine.id}`} className="border border-white/10 rounded-sm bg-white/[0.015]">
      <div className="flex items-center gap-3 px-3 py-2.5">
        <button type="button" onClick={() => setOpen(!open)} data-testid={`am-engine-toggle-${engine.id}`} className="flex-1 min-w-0 flex items-center gap-2 text-left">
          {open ? <ChevronDown className="w-4 h-4 text-nex-gold" /> : <ChevronRight className="w-4 h-4 text-nex-gold" />}
          <span className="font-serif text-sm font-bold text-slate-100">{engine.label}</span>
          <span className="font-mono text-[10px] text-nex-muted">{count}/{ids.length} cards</span>
        </button>
        <label className="inline-flex items-center gap-1.5 text-[11px] text-nex-muted"><Checkbox disabled={locked} checked={seeAll} onCheckedChange={(v) => setMany(ids, v ? "view" : null, true)} data-testid={`am-see-all-${engine.id}`} />See all</label>
        <label className="inline-flex items-center gap-1.5 text-[11px] text-nex-muted"><Checkbox disabled={locked} checked={editAll} onCheckedChange={(v) => setMany(ids, v ? "edit" : "view", false)} data-testid={`am-edit-all-${engine.id}`} />Edit all</label>
      </div>
      {open && <ul>{engine.cards.map((c) => <CardRow key={c.id} card={c} level={access[c.id]} set={set} locked={locked} />)}</ul>}
    </div>
  );
}
